import { useEffect } from 'react'
import { useSearchParams } from '@remix-run/react'

const VARIANTS = [
	{ key: 'A', name: 'Movable float' },
	{ key: 'B', name: 'Dock to hover' },
	{ key: 'C', name: 'Top capture bar' },
] as const

export type VariantKey = (typeof VARIANTS)[number]['key']

export function usePrototypeVariant(): VariantKey {
	const [params] = useSearchParams()
	const v = params.get('variant')?.toUpperCase()
	if (v === 'A' || v === 'B') return v
	return 'C'
}

/** PROTOTYPE ONLY — hidden in production builds. */
export function PrototypeSwitcher() {
	const [params, setParams] = useSearchParams()
	const current = usePrototypeVariant()

	const cycle = (dir: -1 | 1) => {
		const i = VARIANTS.findIndex((v) => v.key === current)
		const next = VARIANTS[(i + dir + VARIANTS.length) % VARIANTS.length]!
		const nextParams = new URLSearchParams(params)
		nextParams.set('variant', next.key)
		setParams(nextParams, { replace: true })
	}

	useEffect(() => {
		if (process.env.NODE_ENV === 'production') return
		const onKey = (e: KeyboardEvent) => {
			const t = e.target as HTMLElement | null
			if (
				t &&
				(t.tagName === 'INPUT' ||
					t.tagName === 'TEXTAREA' ||
					t.isContentEditable)
			) {
				return
			}
			if (e.key === 'ArrowLeft') cycle(-1)
			if (e.key === 'ArrowRight') cycle(1)
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	})

	if (process.env.NODE_ENV === 'production') return null

	const meta = VARIANTS.find((v) => v.key === current)!

	return (
		<div className="fixed bottom-4 left-1/2 z-[100] flex -translate-x-1/2 items-center gap-2 rounded-full border-2 border-amber-400 bg-zinc-950 px-3 py-2 text-sm text-amber-50 shadow-xl">
			<button
				type="button"
				className="rounded-full px-2 py-1 hover:bg-zinc-800"
				onClick={() => cycle(-1)}
			>
				←
			</button>
			<span className="min-w-[11rem] text-center font-semibold">
				{meta.key} — {meta.name}
			</span>
			<button
				type="button"
				className="rounded-full px-2 py-1 hover:bg-zinc-800"
				onClick={() => cycle(1)}
			>
				→
			</button>
		</div>
	)
}
